<template>
  <div class="table-head">
    <a-form-model class="form-appModal" ref="promoAppsForm" :model="form">
      <div class="d-flex flex-column">
        <div v-if="!readonly" class="float-right">
          <p class="font-size-16 font-weight-light">Select application</p>
          <a-form-model-item label="Platform type" :colon="false">
            <a-radio-group
              v-model="listType"
            >
              <a-radio value="mobile">Mobile</a-radio>
              <a-radio value="web">Web</a-radio>
            </a-radio-group>
          </a-form-model-item>
        </div>
        <a-table
          :pagination="false"
          :columns="appsColumns"
          :row-key="(record) => record.application_id"
          :row-selection="readonly ? null : rowSelection"
          :data-source="filteredApps"
          :scroll="{ y: 550 }"
        >
          <template v-if="record?.platform" slot="app" slot-scope="app, record">
            <div class="d-flex align-items-center">
              <a-icon v-if="record?.platform?.toLowerCase() === 'android'" type="android" class="text-success" />
              <a-icon v-if="record?.platform?.toLowerCase() === 'ios'" type="apple" class="text-primary" />
              <a-icon v-if="record?.platform?.toLowerCase() === 'web'" type="global" class="text-default" />
              <span class="ml-2">{{ record.name }}</span>
            </div>
          </template>
          <template v-if="record?.bundle_name" slot="bundle" slot-scope="bundle, record">
            <span>
              {{ record.bundle_name }}
            </span>
          </template>
          <template slot="min_app_version" slot-scope="min_app_version, record">
            <a-input :disabled="isAppDisabled(record.application_id) || readonly" v-model="record.min_app_version" placeholder="0.0.0" />
          </template>
          <template slot="max_app_version" slot-scope="max_app_version, record">
            <div class="d-flex align-items-center">
              <a-input :disabled="isAppDisabled(record.application_id) || readonly" v-model="record.max_app_version" placeholder="999.9" class="mr-2" />
              <div v-if="!readonly" class="clear app" @click="clearApp(record)">
                <a-icon type="delete" class="text-red" />
              </div>
            </div>
          </template>
        </a-table>
        <div v-if="!this.isFormEmpty" class="invalidApps">
          <p>Please select at least one application</p>
        </div>
      </div>

      <a-row class="footer-modal">
        <a-col class="text-right" :span="24">
          <a-button @click="closeModal"> Cancel </a-button>
          <a-button
            v-if="!readonly"
            :loading="submitLoading"
            type="primary"
            @click="onSubmit"
            class="ml-2"
          >
            {{ modalAppsData ? 'Save' : 'Add' }}
          </a-button>
        </a-col>
      </a-row>
    </a-form-model>
  </div>
</template>
<script>
import apiClient from '@/services/axios'

const appsColumns = [
  {
    title: 'App',
    dataIndex: 'app',
    scopedSlots: { customRender: 'app' },
    customHeaderCell: (column) => ({
      class: 'table-header',
    }),
  },
  {
    title: 'Bundle ID',
    dataIndex: 'bundle',
    scopedSlots: { customRender: 'bundle' },
    customHeaderCell: (column) => ({
      class: 'table-header',
    }),
  },
  {
    title: 'Min. ver.',
    dataIndex: 'min_app_version',
    scopedSlots: { customRender: 'min_app_version' },
    customHeaderCell: (column) => ({
      class: 'table-header',
    }),
  },
  {
    title: 'Max. ver.',
    dataIndex: 'max_app_version',
    scopedSlots: { customRender: 'max_app_version' },
    customHeaderCell: (column) => ({
      class: 'table-header',
    }),
  },
]

export default {
  props: {
    modalAppsData: {
      type: Array,
      default: () => [],
    },
    disabledApps: {
      type: Array,
      default: () => [],
    },
    availableApplications: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    scroll: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    submitLoading: false,
    deleteLoading: false,
    form: {
      applications: [],
      minVersion: '',
      maxVersion: '',
    },
    apps: [],
    webAppIds: [],
    appsColumns,
    selectedRowKeys: [],
    selectedData: [],
    isFormEmpty: true,
    listType: 'mobile',
  }),
  computed: {
    rowSelection() {
      return {
        selectedRowKeys: this.selectedRowKeys,
        onChange: this.onSelectChange,
        getCheckboxProps: (record) => ({
          props: {
            disabled: this.isAppDisabled(record.application_id),
          },
        }),
      }
    },
    filteredApps() {
      if (this.listType === 'web') {
        return this.apps.filter(app => this.webAppIds.includes(app.application_id))
      }
      return this.apps.filter(app => !this.webAppIds.includes(app.application_id))
    },
    disabledAppsIds() {
      return this.disabledApps.map(
        (app) => app.application_id,
      )
    },
  },
  methods: {
    onSelectChange(selectedRowKeys) {
      this.selectedRowKeys = selectedRowKeys
      this.selectedData = this.getSelectedData()
      this.isFormEmpty = true
    },
    getSelectedData() {
      return this.apps.filter(app => this.selectedRowKeys.includes(app.application_id))
    },
    appDataFilling() {
      const keys = this.modalAppsData.map(app => app.application_id)
      const hasWebApp = keys.some(id => this.webAppIds.includes(id))

      this.listType = hasWebApp ? 'web' : 'mobile'
      this.selectedRowKeys = keys
      keys.forEach(applicationId => {
        const matchedApp = this.modalAppsData.find(app => app.application_id === applicationId)
        if (matchedApp) {
          const appIndex = this.apps.findIndex(app => app.application_id === applicationId)
          if (appIndex !== -1) {
            const target = this.apps[appIndex]

            this.$set(target, 'min_app_version', matchedApp.min_app_version)
            this.$set(target, 'max_app_version', matchedApp.max_app_version)
          }
        }
      })
      this.onSelectChange(this.selectedRowKeys)
    },
    onSubmit() {
      const appsToSubmit = this.listType === 'web'
        ? this.selectedData.filter(app => this.webAppIds.includes(app.application_id))
        : this.selectedData.filter(app => !this.webAppIds.includes(app.application_id))

      if (!appsToSubmit.length) {
        this.isFormEmpty = false
        return false
      }
      this.$refs.promoAppsForm.validate((valid) => {
        if (valid) {
          this.isFormEmpty = true
          this.$emit('selectApps', appsToSubmit)
        } else {
          return false
        }
      })
    },
    clearApp(app) {
      app.max_app_version = ''
      app.min_app_version = ''
    },
    closeModal() {
      this.$emit('close')
    },
    async fetchApplications() {
      this.appsLoading = true
      try {
        const url = '/admin/applications'
        const response = await apiClient.get(url)
        if (response.data.data) {
          this.setApps(response.data.data)
        }
      } finally {
        this.appsLoading = false
      }
    },
    setApps(apps) {
      this.apps = apps
      const webApps = this.apps.filter(app => app.platform === 'web')
      if (webApps.length > 0) {
        this.webAppIds = webApps.map(app => app.application_id)
      }
    },
    isAppDisabled(appId) {
      return false
    },
  },
  async mounted() {
    if (!this.availableApplications?.length) {
      await this.fetchApplications()
    } else {
      this.setApps(this.availableApplications)
    }

    if (this.modalAppsData) {
      this.appDataFilling()
    }
  },
}
</script>

<style lang="scss" scoped>

.table-head .ant-table-thead>tr>th {
  background-color: #fafafa !important;
  font-weight: 600 !important;
}

.disabled-row {
  background-color: #fafafa;
  color: #595959;
  font-weight: 400;
}

.form-appModal {
  .ant-select-selection {
    background-color: transparent !important;
    border: 0 !important;
  }
}

.checkbox-list {
  display: flex !important;
  flex-wrap: wrap;

  &__item {
    flex: 1 1 30%;
    max-width: 31%;
    padding: 5px 0 !important;
  }
}

.invalidApps {
  margin-top: 1rem;
  color: #f5222e;
  clear: both;
  min-height: 22px;
  font-size: 14px;
  line-height: 1.5;
  -webkit-transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
  transition: color 0.3s cubic-bezier(0.215, 0.61, 0.355, 1);
}

::v-deep .ant-form-item-control {
  line-height: normal;
}
</style>
